import { useCallback } from 'react';

import { useSelectionCount } from '@confluence/content-tree-actions/entry-points/useSelectionCount.ts';
import { useContentTreeSelection } from '@confluence/content-tree-actions/entry-points/store.ts';
import type { ItemId } from '@confluence/tree/entry-points/tree-types.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { usePageTreeActions } from '../usePageTreeState.ts';
import type { ContentTreeItem } from '../data-transformers.ts';

type ItemWithParent = { id: ItemId; parentId?: ItemId };

export const useNestedSelect = () => {
	const [{ selectedContent }, { selectNestedContent }] = useContentTreeSelection();

	const { getState } = usePageTreeActions();

	const getAncestorIds = useCallback(
		(itemId: ItemId) => {
			const { pages, rootId } = getState();
			const ancestorContentIds = new Set<ItemId>();

			const getIds = (currItem) => {
				const parentId = currItem.parentId;

				if (parentId && parentId != rootId) {
					ancestorContentIds.add(currItem.parentId);
					getIds(pages[parentId]);
				}
			};

			getIds(pages[itemId]);

			return ancestorContentIds;
		},
		[getState],
	);

	const updateDescendantCount = useSelectionCount();

	const selectContentAndDescendants = useCallback(
		(itemId: ItemId, isExplicitlySelected: boolean, flags: FlagsStateContainer) => {
			const { pages } = getState();
			const descendants: ItemWithParent[] = [];

			const isPreviouslySelected = itemId in selectedContent;
			const isOnExpandOnSelectedParent = isPreviouslySelected && !isExplicitlySelected;

			const pageTreeItem = getState().pages[itemId];

			// Remove all of the count that was previously selected in this tree, so it is not counted twice.
			let countToRemove: number = 0;

			const traverse = (currItem: ContentTreeItem, isCountRemoved: boolean) => {
				let currIsCountRemoved = isCountRemoved;
				if (
					currItem &&
					currItem.id != pageTreeItem.id &&
					currItem.id in selectedContent &&
					!currIsCountRemoved
				) {
					countToRemove += selectedContent[currItem.id]?.selectedDescendantCount + 1;
					currIsCountRemoved = true;
				}

				descendants.push({ id: currItem.id, parentId: currItem.parentId ?? undefined });

				const children = currItem.children || [];
				for (const childId of children) {
					const child = pages[childId];
					if (child && child.data.status != 'DRAFT') {
						traverse(child, currIsCountRemoved);
					}
				}
			};

			if ((!isPreviouslySelected && isExplicitlySelected) || isOnExpandOnSelectedParent) {
				traverse(pageTreeItem, false);

				// selection
				selectNestedContent(descendants);

				// count
				updateDescendantCount(
					pageTreeItem,
					[...getAncestorIds(itemId)],
					[pageTreeItem.id, ...descendants.map((item) => item.id)],
					isExplicitlySelected,
					countToRemove,
					flags,
				);
			}
		},
		[getAncestorIds, getState, selectNestedContent, selectedContent, updateDescendantCount],
	);

	return selectContentAndDescendants;
};
